/**
 * Authentication Service
 * Handles all authentication-related API calls
 */

import axios from '../api/axios';
import { API_ENDPOINTS } from './api.config';
import { handleApiError } from '../utils/errorHandler';

class AuthService {
  /**
   * Send OTP to mobile number
   * @param {string} mobile - 10-digit mobile number
   * @returns {Promise<Object>} Response with message
   */
  async sendOTP(mobile) {
    try {
      const response = await axios.post(API_ENDPOINTS.AUTH.SEND_OTP, {
        mobile: mobile,
      });
      return response.data;
    } catch (error) {
      throw handleApiError(error, 'Failed to send OTP');
    }
  }

  /**
   * Verify OTP
   * @param {string} mobile - 10-digit mobile number
   * @param {string} otp - 6-digit OTP
   * @returns {Promise<Object>} Response with message
   */
  async verifyOTP(mobile, otp) {
    try {
      const response = await axios.post(API_ENDPOINTS.AUTH.VERIFY_OTP, {
        mobile: mobile,
        otp: otp,
      });
      return response.data;
    } catch (error) {
      throw handleApiError(error, 'Failed to verify OTP');
    }
  }

  /**
   * Sign up new user
   * @param {Object} userData - User registration data
   * @param {string} userData.mobile - 10-digit mobile number
   * @param {string} userData.password - User password
   * @param {string} userData.username - User's full name
   * @param {string} userData.email - User's email (optional)
   * @returns {Promise<Object>} Response with tokens
   */
  async signup(userData) {
    try {
      const response = await axios.post(API_ENDPOINTS.AUTH.SIGNUP, {
        mobile: userData.mobile,
        name: userData.name,
        password: userData.password,
        otp: userData.otp,
      });

      // Store tokens in localStorage
      if (response.data.tokens) {
        localStorage.setItem('access_token', response.data.tokens.access);
        localStorage.setItem('refresh_token', response.data.tokens.refresh);
      }

      return response.data;
    } catch (error) {
      throw handleApiError(error, 'Signup failed');
    }
  }

  /**
   * Login user
   * @param {string} mobile - 10-digit mobile number
   * @param {string} password - User password
   * @returns {Promise<Object>} Response with tokens
   */
  async login(mobile, password) {
    try {
      const response = await axios.post(API_ENDPOINTS.AUTH.LOGIN, {
        mobile: mobile,
        password: password,
      });

      // Store tokens in localStorage
      if (response.data.tokens) {
        localStorage.setItem('access_token', response.data.tokens.access);
        localStorage.setItem('refresh_token', response.data.tokens.refresh);
      }

      return response.data;
    } catch (error) {
      throw handleApiError(error, 'Invalid credentials');
    }
  }

  /**
   * Forgot/Reset password
   * @param {string} mobile - 10-digit mobile number
   * @param {string} newPassword - New password
   * @param {string} otp - 6-digit OTP
   * @returns {Promise<Object>} Response with message
   */
  async forgotPassword(mobile, newPassword, otp) {
    try {
      const response = await axios.post(API_ENDPOINTS.AUTH.FORGOT_PASSWORD, {
        mobile: mobile,
        otp: otp,
        new_password: newPassword,
        confirm_password: newPassword,
      });
      return response.data;
    } catch (error) {
      throw handleApiError(error, 'Failed to reset password');
    }
  }

  /**
   * Get user profile
   * @returns {Promise<Object>} User profile data
   */
  async getProfile() {
    try {
      const response = await axios.get(API_ENDPOINTS.AUTH.PROFILE);
      return response.data;
    } catch (error) {
      throw handleApiError(error, 'Failed to fetch profile');
    }
  }

  /**
   * Update user profile
   * @param {Object} profileData - Profile data to update
   * @param {string} profileData.email - Email address (optional)
   * @param {string} profileData.alternate_mobile - Alternate mobile (optional)
   * @param {string} profileData.date_of_birth - Date of birth YYYY-MM-DD (optional)
   * @param {string} profileData.gender - Gender (male/female/other) (optional)
   * @param {string} profileData.blood_group - Blood group (optional)
   * @param {string} profileData.emergency_contact - Emergency contact number (optional)
   * @param {File} profileData.profile_pic - Profile picture file (optional)
   * @returns {Promise<Object>} Response with message
   */
  async updateProfile(profileData) {
    try {
      const formData = new FormData();

      // Add all fields to FormData
      Object.keys(profileData).forEach((key) => {
        if (profileData[key] !== null && profileData[key] !== undefined) {
          formData.append(key, profileData[key]);
        }
      });

      const response = await axios.post(
        API_ENDPOINTS.AUTH.UPDATE_PROFILE,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      return response.data;
    } catch (error) {
      throw handleApiError(error, 'Failed to update profile');
    }
  }

  /**
   * Logout user
   * Clears all authentication data from localStorage
   */
  logout() {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
  }

  /**
   * Check if user is authenticated
   * @returns {boolean} Authentication status
   */
  isAuthenticated() {
    return !!localStorage.getItem('access_token');
  }

  /**
   * Get access token
   * @returns {string|null} Access token
   */
  getAccessToken() {
    return localStorage.getItem('access_token');
  }

  /**
   * Handle API errors
   * @param {Error} error - Error object
   * @returns {Error} Formatted error
   */
  handleError(error) {
    if (error.response) {
      // Server responded with error
      const errorMessage = error.response.data.error || error.response.data.message || 'Something went wrong';
      return new Error(errorMessage);
    } else if (error.request) {
      // Request made but no response
      return new Error('Network error. Please check your connection.');
    } else {
      // Error in request setup
      return new Error('An error occurred. Please try again.');
    }
  }
}

export default new AuthService();
